<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import { useI18n } from 'vue-i18n';
import { CONTENT_TYPES } from '../constants.js';
import { useMessageContext } from '../provider.js';
import { useInbox } from 'dashboard/composables/useInbox';

const { content, contentAttributes, contentType } = useMessageContext();
const { t } = useI18n();
const { isAWebWidgetInbox } = useInbox();

const formValues = computed(() => {
  if (contentType.value === CONTENT_TYPES.FORM) {
    const { items, submittedValues = [] } = contentAttributes.value;

    if (submittedValues.length) {
      return submittedValues.map(submittedValue => {
        const item = items.find(
          formItem => formItem.name === submittedValue.name
        );
        return {
          title: submittedValue.value,
          value: submittedValue.value,
          label: item?.label,
        };
      });
    }

    return [];
  }

  if (contentType.value === CONTENT_TYPES.INPUT_SELECT) {
    const [item] = contentAttributes.value?.submittedValues ?? [];
    if (!item) return [];

    return [
      {
        title: item.title,
        value: item.value,
        label: '',
      },
    ];
  }

  return [];
});

const interactiveOptions = computed(() => {
  if (contentType.value !== CONTENT_TYPES.INPUT_SELECT) return [];
  if (formValues.value.length) return [];

  const items = contentAttributes.value?.items;
  if (!Array.isArray(items)) return [];

  return items.filter(item => item?.title);
});
</script>

<template>
  <BaseBubble
    neutral-surface
    class="w-80 !max-w-full px-3 py-3"
    data-bubble-name="form"
  >
    <span v-dompurify-html="content" :title="content" />
    <dl v-if="formValues.length" class="mt-3 space-y-2">
      <div
        v-for="item in formValues"
        :key="item.title"
        class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12"
      >
        <dt class="text-xs font-medium text-n-slate-11">
          {{ item.label || t('CONVERSATION.RESPONSE') }}
        </dt>
        <dd class="mt-1 break-words text-sm">{{ item.title }}</dd>
      </div>
    </dl>
    <div
      v-else-if="interactiveOptions.length"
      class="mt-3 grid gap-2"
      :aria-label="t('CONVERSATION.INTERACTIVE_OPTIONS')"
    >
      <span
        v-for="item in interactiveOptions"
        :key="item.value || item.title"
        data-interactive-option
        class="flex min-h-9 cursor-default items-center justify-center gap-2 rounded-lg border border-current/20 bg-white/10 px-3 py-2 text-center text-sm font-medium"
      >
        <span class="i-lucide-reply size-4 shrink-0" aria-hidden="true" />
        {{ item.title }}
      </span>
    </div>
    <div v-else-if="isAWebWidgetInbox" class="my-2 font-medium">
      {{ t('CONVERSATION.NO_RESPONSE') }}
    </div>
  </BaseBubble>
</template>
